import type { MonetaryValue } from 'ezmoney';
import * as ezmoney from 'ezmoney';
import isNil from 'lodash/isNil';

import { sumMonetaryValues } from 'src/core/common/utils/monetaryValue';

import { type DeliveryNotesStatuses } from './deliveryNote';
import type {
  OpenedPurchaseOrder,
  ClosedPurchaseOrder,
  BillingStatus,
  BillingOverDeliveryStatus,
  LineItemSummaries,
} from './purchaseOrder';
import { type LineItem } from '../../requests/invoice/creation/ManageLineItemsModal/types';
import type { PurchaseOrderDetails } from '../detail/models';

export type ThreeWayMatchStatus = {
  billingStatus: BillingStatus;
  deliveryStatus: DeliveryNotesStatuses;
  billingOverDeliveryStatus: BillingOverDeliveryStatus;
};

export type ThreeWayMatchAmounts = {
  purchaseOrderAmount: MonetaryValue;
  billedAmount: MonetaryValue;
  deliveredAmount: MonetaryValue;
};

const computeInvoiceBilledAmount = (
  invoiceAmount: MonetaryValue,
  lineItems: LineItem[],
  currency: string,
  purchaseOrderHasLineItems: boolean,
): MonetaryValue => {
  if (!purchaseOrderHasLineItems) {
    return invoiceAmount;
  }

  const linkedLineItems = lineItems.filter(
    (item) => item.purchaseOrderRequestItemId,
  );
  if (linkedLineItems.length === 0) {
    return ezmoney.fromNumber(0, currency, 2);
  }
  return linkedLineItems.reduce(
    (sum: MonetaryValue, item: LineItem) =>
      ezmoney.add(sum, computeLineItemTotalAmountWithTax(item, currency)),
    ezmoney.fromNumber(0, currency, 2),
  );
};

export const computeForecastedThreeWayMatchAmounts = (
  purchaseOrder: PurchaseOrderDetails,
  invoiceUpdate: {
    requestId?: string;
    newAmount: MonetaryValue;
    newLineItems?: LineItem[];
    oldLineItems?: LineItem[];
  },
): ThreeWayMatchAmounts => {
  const linkedInvoice = invoiceUpdate.requestId
    ? purchaseOrder.invoices.find(
        (attachedInvoice) =>
          invoiceUpdate.requestId === attachedInvoice.requestId,
      )
    : undefined;

  const purchaseOrderHasLineItems = !!purchaseOrder.items?.length;

  const effectiveNewAmount = computeInvoiceBilledAmount(
    invoiceUpdate.newAmount,
    invoiceUpdate.newLineItems ?? [],
    purchaseOrder.amount.currency,
    purchaseOrderHasLineItems,
  );

  const oldLinkedInvoiceAmount = linkedInvoice
    ? computeInvoiceBilledAmount(
        linkedInvoice.amount,
        invoiceUpdate.oldLineItems ?? [],
        linkedInvoice.amount.currency,
        purchaseOrderHasLineItems,
      )
    : ezmoney.fromNumber(0, purchaseOrder.amount.currency, 2);

  const forecastedBilledAmount = ezmoney.add(
    ezmoney.subtract(purchaseOrder.billedAmount, oldLinkedInvoiceAmount),
    effectiveNewAmount,
  );

  return {
    purchaseOrderAmount: purchaseOrder.amount,
    billedAmount: forecastedBilledAmount,
    deliveredAmount: purchaseOrder.deliveredAmount,
  };
};

export const computeForecastedThreeWayMatch = (
  purchaseOrder: PurchaseOrderDetails &
    (OpenedPurchaseOrder | ClosedPurchaseOrder),
  invoiceUpdate: {
    requestId?: string;
    newAmount: MonetaryValue;
    oldLineItems?: LineItem[];
    newLineItems?: LineItem[];
  },
): {
  threeWayMatchStatus: ThreeWayMatchStatus;
  threeWayMatchAmounts: ThreeWayMatchAmounts;
} => {
  const forecastedThreeWayMatchAmounts = computeForecastedThreeWayMatchAmounts(
    purchaseOrder,
    invoiceUpdate,
  );

  const forecastedThreeWayMatchStatus: ThreeWayMatchStatus = {
    billingStatus: computeForecastedBillingStatus(
      forecastedThreeWayMatchAmounts,
    ),
    deliveryStatus: purchaseOrder.deliveryStatus,
    billingOverDeliveryStatus: computeForecastedBillingOverDeliveryStatus(
      purchaseOrder,
      forecastedThreeWayMatchAmounts,
    ),
  };

  return {
    threeWayMatchStatus: forecastedThreeWayMatchStatus,
    threeWayMatchAmounts: forecastedThreeWayMatchAmounts,
  };
};

const computeForecastedBillingStatus = ({
  billedAmount,
  purchaseOrderAmount,
}: ThreeWayMatchAmounts): BillingStatus => {
  if (
    ezmoney.equal(billedAmount, ezmoney.fromNumber(0, billedAmount.currency, 2))
  ) {
    return 'waitingForBilling';
  }

  switch (ezmoney.compare(billedAmount, purchaseOrderAmount)) {
    case 1:
      return 'overbilled';
    case 0:
      return 'billed';
    default:
      return 'partiallyBilled';
  }
};

const computeForecastedBillingOverDeliveryStatus = (
  purchaseOrder: OpenedPurchaseOrder | ClosedPurchaseOrder,
  { billedAmount, deliveredAmount }: ThreeWayMatchAmounts,
): BillingOverDeliveryStatus => {
  if (purchaseOrder.deliveryStatus === 'noDeliveryExpected') {
    return 'none';
  }

  switch (ezmoney.compare(billedAmount, deliveredAmount)) {
    case 1:
      return 'overbilled';
    case 0:
      return 'fullybilled';
    default:
      return 'underbilled';
  }
};

export type CurrentInvoice = {
  amount: number;
  requestId?: string;
  invoiceRequestId?: string;
  invoiceNumber?: string;
  lineItems?: LineItem[];
};

export const mergeLineItems = (
  lineItemsSummary: LineItemSummaries,
  currentInvoice: CurrentInvoice,
): LineItemSummaries => {
  const mergedPurchaseOrderLines = lineItemsSummary.purchaseOrderLines.map(
    (line) => {
      const lineItemsBelongingToCurrentInvoice = line.invoices.filter(
        (invoice) => currentInvoice.requestId === invoice.requestId,
      );

      const lineItemsNotBelongingToCurrentInvoice = line.invoices.filter(
        (invoice) => currentInvoice.requestId !== invoice.requestId,
      );

      if (
        !!lineItemsBelongingToCurrentInvoice.length &&
        !currentInvoice?.lineItems
      ) {
        return {
          ...line,
          invoices: [
            ...lineItemsBelongingToCurrentInvoice.map((item) => ({
              ...item,
              tag: 'currentInvoice' as const,
            })),
            ...lineItemsNotBelongingToCurrentInvoice,
          ],
        };
      }

      // FIXME @invoice-automation: it seems that we only have invoice.totalPriceWithTax, but we do not have the tax rate,
      // so we cannot compute the amountToDeductWithTax vs amountToDeductWithoutTax
      const amountToDeduct = sumMonetaryValues(
        line.currency,
        lineItemsBelongingToCurrentInvoice.map((invoice) =>
          ezmoney.fromNumber(invoice.totalPriceWithTax, line.currency, 2),
        ),
      );

      const quantityToDeduct = lineItemsBelongingToCurrentInvoice.reduce(
        (accumulator, invoice) => {
          return accumulator + invoice.unitQuantity;
        },
        0,
      );

      const currentInvoiceRow = (currentInvoice.lineItems ?? []).find(
        (item) => item.purchaseOrderRequestItemId === line.id,
      );

      if (currentInvoiceRow) {
        const amountToAddWithoutTax = ezmoney.fromNumber(
          currentInvoiceRow.unitPriceWithoutTax *
            currentInvoiceRow.unitQuantity,
          line.currency,
          2,
        );

        const amountToAddWithTax = ezmoney.fromNumber(
          currentInvoiceRow.unitPriceWithoutTax *
            (1 + currentInvoiceRow.taxRate / 100) *
            currentInvoiceRow.unitQuantity,
          line.currency,
          2,
        );

        const currentLineItem = {
          invoiceNumber: currentInvoice.invoiceNumber,
          invoiceRequestId: currentInvoice.invoiceRequestId,
          requestId: currentInvoice.requestId,
          totalPriceWithTax:
            currentInvoiceRow.unitPriceWithoutTax *
            (1 + currentInvoiceRow.taxRate / 100) *
            currentInvoiceRow.unitQuantity,
          unitPriceWithTax:
            currentInvoiceRow.unitPriceWithoutTax *
            (1 + currentInvoiceRow.taxRate / 100),
          unitQuantity: currentInvoiceRow.unitQuantity,
          tag: 'currentInvoice' as const,
        };

        return {
          ...line,
          invoices: [currentLineItem, ...lineItemsNotBelongingToCurrentInvoice],
          amountBilledWithoutTax: ezmoney.add(
            ezmoney.subtract(line.amountBilledWithoutTax, amountToDeduct),
            amountToAddWithoutTax,
          ),
          amountBilledWithTax: ezmoney.add(
            ezmoney.subtract(line.amountBilledWithTax, amountToDeduct),
            amountToAddWithTax,
          ),
          quantityBilled:
            line.quantityBilled -
            quantityToDeduct +
            currentInvoiceRow.unitQuantity,
        };
      }

      return {
        ...line,
        invoices: lineItemsNotBelongingToCurrentInvoice,
        amountBilledWithoutTax: ezmoney.subtract(
          line.amountBilledWithoutTax,
          amountToDeduct,
        ),
        amountBilledWithTax: ezmoney.subtract(
          line.amountBilledWithTax,
          amountToDeduct,
        ),
        quantityBilled: line.quantityBilled - quantityToDeduct,
      };
    },
  );

  return {
    ...lineItemsSummary,
    purchaseOrderLines: mergedPurchaseOrderLines,
  };
};

export const extractInvoiceOutstandingItems = (
  lineItems: LineItem[],
): LineItem[] => {
  return lineItems.filter(isOutstandingLineItem);
};

export const isOutstandingLineItem = (lineItem: LineItem): boolean => {
  return isNil(lineItem.purchaseOrderRequestItemId);
};

export const computeLineItemAmountWithTax = (
  lineItem: LineItem,
  currency: string,
): MonetaryValue => {
  const amountWithoutTax = ezmoney.fromNumber(
    lineItem.unitPriceWithoutTax,
    currency,
    2,
  );

  return ezmoney.unsafeMultiply(amountWithoutTax, 100 + lineItem.taxRate, 2);
};

export const computeLineItemTotalAmountWithTax = (
  lineItem: LineItem,
  currency: string,
): MonetaryValue => {
  const amountWithTax = computeLineItemAmountWithTax(lineItem, currency);

  const quantityWithDecimals = ezmoney.fromNumber(
    lineItem.unitQuantity,
    currency,
    4,
  );

  return ezmoney.multiply(
    amountWithTax,
    quantityWithDecimals.amount,
    quantityWithDecimals.precision,
  );
};
